/**
 * 任务列表/详情共用的状态展示与跳转辅助
 */
import type { Router } from 'vue-router'

import { TaskPurpose, TaskStatus, ProjectType } from '@/api/common-types'
import { getWorkItem } from '@/modules/project/api/work-item'
import type { TaskResponse } from '@/modules/task/api/task'

export type ReceivedInboxTab =
  | 'pending_accept'
  | 'in_review'
  | 'submitted'
  | 'ended'

export type AssignedInboxTab = 'waiting_other' | 'my_review' | 'ended'

export function statusesForInboxTab(
  mode: 'received' | 'assigned',
  tab: string,
): TaskStatus[] {
  if (mode === 'received') {
    switch (tab as ReceivedInboxTab) {
      case 'pending_accept':
        return [TaskStatus.PENDING]
      case 'in_review':
        return [TaskStatus.PROCESSING]
      case 'submitted':
        return [TaskStatus.SUBMITTED]
      case 'ended':
        return [TaskStatus.COMPLETED, TaskStatus.REJECTED_AT_ASSIGNMENT]
      default:
        return [TaskStatus.PENDING]
    }
  }
  switch (tab as AssignedInboxTab) {
    case 'waiting_other':
      return [TaskStatus.PENDING, TaskStatus.PROCESSING]
    case 'my_review':
      return [TaskStatus.SUBMITTED]
    case 'ended':
      return [TaskStatus.COMPLETED, TaskStatus.REJECTED_AT_ASSIGNMENT]
    default:
      return [TaskStatus.SUBMITTED]
  }
}

/** 单状态页签可走 API status；多状态需拉大页再客户端过滤 */
export function apiStatusForInboxTab(
  mode: 'received' | 'assigned',
  tab: string,
): TaskStatus | undefined {
  const list = statusesForInboxTab(mode, tab)
  return list.length === 1 ? list[0] : undefined
}

export function filterTasksByInboxTab(
  mode: 'received' | 'assigned',
  tab: string,
  items: TaskResponse[],
): TaskResponse[] {
  const allowed = new Set(statusesForInboxTab(mode, tab).map(String))
  return items.filter((item) => allowed.has(String(item.status)))
}

export function countByInboxTab(
  mode: 'received' | 'assigned',
  items: TaskResponse[],
): Record<string, number> {
  const tabs =
    mode === 'received'
      ? (['pending_accept', 'in_review', 'submitted', 'ended'] as const)
      : (['waiting_other', 'my_review', 'ended'] as const)
  const counts: Record<string, number> = {}
  for (const tab of tabs) {
    counts[tab] = filterTasksByInboxTab(mode, tab, items).length
  }
  return counts
}

/** 统一页签过滤 + 可选条目 ID + 客户端分页 */
export function buildInboxListPage(opts: {
  mode: 'received' | 'assigned'
  tab: string
  items: TaskResponse[]
  workItemId?: number
  page: number
  pageSize: number
  needClientFilter: boolean
  serverTotal?: number
}): { items: TaskResponse[]; total: number } {
  let items = filterTasksByInboxTab(opts.mode, opts.tab, opts.items)
  if (opts.workItemId != null) {
    items = items.filter((item) => item.work_item_id === opts.workItemId)
  }
  if (opts.needClientFilter) {
    const total = items.length
    const start = (opts.page - 1) * opts.pageSize
    return {
      items: items.slice(start, start + opts.pageSize),
      total,
    }
  }
  return { items, total: opts.serverTotal ?? items.length }
}

export function inboxListQueryFlags(
  mode: 'received' | 'assigned',
  tab: string,
  workItemId?: number,
): { apiStatus: TaskStatus | undefined; needClientFilter: boolean } {
  const apiStatus = apiStatusForInboxTab(mode, tab)
  return {
    apiStatus,
    needClientFilter: apiStatus == null || workItemId != null,
  }
}

export function parseWorkItemIdKeyword(keyword?: string | null): number | undefined {
  const trimmed = keyword?.trim()
  return trimmed && /^\d+$/.test(trimmed) ? Number(trimmed) : undefined
}

export const TASK_STATUS_FILTER_OPTIONS: TaskStatus[] = [
  TaskStatus.PENDING,
  TaskStatus.PROCESSING,
  TaskStatus.SUBMITTED,
  TaskStatus.COMPLETED,
  TaskStatus.REJECTED_AT_ASSIGNMENT,
]

export function getTaskStatusColor(status: string): string {
  const colors: Record<string, string> = {
    [TaskStatus.PENDING]: 'orange',
    [TaskStatus.PROCESSING]: 'blue',
    [TaskStatus.SUBMITTED]: 'cyan',
    [TaskStatus.COMPLETED]: 'green',
    [TaskStatus.REJECTED_AT_ASSIGNMENT]: 'red',
  }
  return colors[status] || 'default'
}

export function getTaskStatusI18nKey(status: string): string {
  const keys: Record<string, string> = {
    [TaskStatus.PENDING]: 'pending',
    [TaskStatus.PROCESSING]: 'processing',
    [TaskStatus.SUBMITTED]: 'submitted',
    [TaskStatus.COMPLETED]: 'completed',
    [TaskStatus.REJECTED_AT_ASSIGNMENT]: 'rejectedAtAssignment',
  }
  return keys[status] || status
}

export function getTaskPurposeI18nKey(purpose: string): string {
  const normalized = String(purpose || '').toUpperCase()
  if (normalized === TaskPurpose.TRANSLATE || normalized === 'TRANSLATE') {
    return 'purposeTranslate'
  }
  if (normalized === TaskPurpose.REVIEW || normalized === 'REVIEW') {
    return 'purposeReview'
  }
  return 'purposeUnknown'
}

export function getTaskDisplayTitle(task: TaskResponse, unnamedLabel: string): string {
  if (task.title?.trim()) return task.title.trim()
  if (task.project_name?.trim()) return task.project_name.trim()
  return unnamedLabel
}

/** 任务列表/详情用户展示：姓名（备注）或回退 #id */
export function formatTaskUserLabel(
  user?: { name?: string | null; name_remark?: string | null; username?: string | null } | null,
  userId?: number | null,
  emptyLabel = '—',
): string {
  if (user?.name?.trim()) {
    const remark = user.name_remark?.trim()
    return remark ? `${user.name.trim()}（${remark}）` : user.name.trim()
  }
  if (user?.username?.trim()) return user.username.trim()
  if (userId != null) return `#${userId}`
  return emptyLabel
}

/** 按条目 pipeline/类型跳转工作区（路由 :id 为 work_item_id） */
export async function openWorkItemWorkspace(
  router: Router,
  workItemId: number,
): Promise<void> {
  const workItem = await getWorkItem(workItemId)
  const { getWorkItemWorkspacePath } = await import(
    '@/modules/project/utils/work-item-navigation'
  )
  await router.push(getWorkItemWorkspacePath(workItem))
}

async function resolveTaskProjectType(
  task: TaskResponse,
): Promise<ProjectType | string | undefined> {
  if (task.project_type) return task.project_type
  const workItem = await getWorkItem(task.work_item_id)
  return workItem.item_type
}

/** 任务中心 accept / 打开工作区：教案进 lesson-review，其它仍按条目 workspace */
export async function openTaskWorkspace(
  router: Router,
  task: TaskResponse,
): Promise<void> {
  const projectType = await resolveTaskProjectType(task)
  if (projectType === ProjectType.LESSON || projectType === 'lesson') {
    await router.push(`/task/lesson-review/${task.id}`)
    return
  }
  await openWorkItemWorkspace(router, task.work_item_id)
}
